import { clone } from 'ramda';
import { reactive, computed } from 'vue';
import { fetchPermissions } from '@/api/user/PermissionApi';

const permissionState = reactive({
  permissions: [],
  isInitialized: false,
});

export const loadPermissions = async () => {
  if (!permissionState.isInitialized) {
    permissionState.permissions = await fetchPermissions();
    permissionState.isInitialized = true;
  }
};

const usePermissionProvider = () => {
  /**
   * Whether the user holds at least one permission on a site.
   *
   * Unlike `isGranted` this never throws for a site the user has no record for, so it
   * can answer "may this user open this site at all?" for pages that are not gated
   * behind a specific feature permission.
   */
  const hasSiteAccess = (siteId, roleIds = []) => {
    const permissionsOfSite = permissionState.permissions.find(
      permissions => permissions.siteId === siteId?.toString(),
    );

    return (permissionsOfSite?.permissionCategories || []).some(
      category => (category.permissionDetails || []).some(
        detail => (detail.roleIds || []).some(roleId => roleIds.includes(roleId.toString())),
      ),
    );
  };

  const getPermissionsOfSite = siteId => {
    const permissionsOfSite = permissionState.permissions.find(
      permissions => permissions.siteId === siteId.toString(),
    );

    const permissionsCategoriesOfSite = permissionsOfSite ? permissionsOfSite.permissionCategories : null;

    if (!permissionsOfSite || !permissionsCategoriesOfSite) {
      throw new Error('No permission data available for this site.');
    }

    return permissionsCategoriesOfSite;
  };

  const isGranted = (siteId, permissionName, roleIds) => {
    const allPermissions = Array.isArray(permissionName) ? permissionName : [permissionName];

    const results = allPermissions.reduce((acc, permissionToCheck) => {
      const draftAcc = [...acc];
      const permissionKeys = permissionToCheck.split('.');

      if (permissionKeys.length !== 2) {
        throw new Error('Invalid permission request.');
      }

      const permissionsCategories = getPermissionsOfSite(siteId);
      const permissionCategoryName = permissionKeys[0];
      const permissionDetailName = permissionKeys[1];
      const permissionCategory = (permissionsCategories || []).find(
        category => category.keyword === permissionCategoryName,
      );

      if (!permissionCategory) {
        draftAcc.push(false);

        return draftAcc;
      }

      const permission = (permissionCategory.permissionDetails || []).find(
        detail => detail.keyword === permissionDetailName,
      );

      if (!permission) {
        draftAcc.push(false);

        return draftAcc;
      }

      draftAcc.push((permission.roleIds || []).some(roleId => roleIds.includes(roleId.toString())));

      return draftAcc;
    }, []);

    return results.some(result => result);
  };

  return {
    permissions: computed(() => clone(permissionState.permissions)),
    isGranted,
    hasSiteAccess,
  };
};

export default usePermissionProvider;
